/**
 * In-memory cache + in-flight dedupe for HPO id → label lookups.
 * Prevents fan-out retries from re-hitting rate-limited search.
 */

type ResolvedTerm = { id: string; lbl: string }

const resolvedCache = new Map<string, ResolvedTerm | null>()
const inflight = new Map<string, Promise<ResolvedTerm | null>>()

function getCachedHpoTerm(id: string): ResolvedTerm | null | undefined {
  if (!resolvedCache.has(id)) return undefined
  return resolvedCache.get(id) ?? null
}

function setCachedHpoTerm(id: string, value: ResolvedTerm | null): void {
  resolvedCache.set(id, value)
}

/** Deduplicate concurrent lookups for the same id. */
export async function withHpoLookupDedupe(
  id: string,
  loader: () => Promise<ResolvedTerm | null>
): Promise<ResolvedTerm | null> {
  const cached = getCachedHpoTerm(id)
  if (cached !== undefined) return cached

  const existing = inflight.get(id)
  if (existing) return existing

  const pending = loader()
    .then((result) => {
      setCachedHpoTerm(id, result)
      return result
    })
    .finally(() => {
      inflight.delete(id)
    })
  inflight.set(id, pending)
  return pending
}

/** Test helper — clears cache between cases. */
export function clearHpoLookupCache(): void {
  resolvedCache.clear()
  inflight.clear()
}

/**
 * Run async work over items with a fixed concurrency cap (keeps under
 * terminology search rate limits when many codes need resolving).
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return []
  const limit = Math.max(1, Math.min(concurrency, items.length))
  const results = new Array<R>(items.length)
  let nextIndex = 0

  const workers = Array.from({ length: limit }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      results[index] = await mapper(items[index], index)
    }
  })
  await Promise.all(workers)
  return results
}
